import { useId } from 'react'
import styles from './TrendPlot.module.css'

// Adapted from BoardUI 0.5.9 line-chart-card.tsx and area-chart-card.tsx:
// a monotone line over a 35%-to-transparent area, dashed grid/cursor and
// a solid active dot with a soft halo. Studio supplies measured geometry;
// the marker stays still and the chart never animates or invents observations.
const stateAttributes = (state) => ({
  'data-active': state === 'active' || undefined,
  'data-muted': state === 'muted' || undefined,
})

function Dot({ point }) {
  return (
    <g>
      {point.halo ? <circle className={styles.activeHalo} cx={point.x} cy={point.y} r={point.r + 4} data-series={point.tone} aria-hidden="true" /> : null}
      <circle
        className={point.previous ? styles.chartDotPrevious : styles.chartDot}
        cx={point.x}
        cy={point.y}
        r={point.r}
        data-series={point.tone}
        data-dashed={point.dashed || undefined}
        {...stateAttributes(point.state)}
      />
    </g>
  )
}

export function BoardTrendPlot({ containerRef, width, height, padding, ticks, labels, lines, dots, area, cursor, markers, label, onMove, onLeave }) {
  const gradientId = useId()
  return (
    <div className={styles.chartPlot} ref={containerRef}>
      <svg className={styles.chartSvg} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label} onMouseMove={onMove} onMouseLeave={onLeave}>
        {area?.path ? (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1" className={styles.gradient} data-tone={area.tone}>
              <stop offset="0%" stopColor="var(--boardui-trend-color)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--boardui-trend-color)" stopOpacity={0} />
            </linearGradient>
          </defs>
        ) : null}
        {ticks.map((tick) => (
          <g key={tick.key}>
            <line className={tick.baseline ? styles.chartBaseline : styles.chartGrid} x1={padding.left} x2={width - padding.right} y1={tick.y} y2={tick.y} />
            <text className={styles.chartAxis} x={padding.left - 6} y={tick.y + 3} textAnchor="end">{tick.label}</text>
          </g>
        ))}
        {labels.map((tick) => <text key={tick.key} className={styles.chartAxis} x={tick.x} y={height - 6} textAnchor={tick.align}>{tick.label}</text>)}
        {area?.path ? <path className={styles.area} d={area.path} fill={`url(#${gradientId})`} data-trend-area="" data-tone={area.tone} {...stateAttributes(area.state)} /> : null}
        {lines.map((line) => (
          <path key={line.key} className={line.previous ? styles.chartLinePrevious : styles.chartLine} d={line.path} data-trend-line="" data-series={line.tone} data-dashed={line.dashed || undefined} {...stateAttributes(line.state)} />
        ))}
        {dots.map((point) => <Dot key={point.key} point={point} />)}
        {cursor ? (
          <g>
            <line className={styles.chartCrosshair} x1={cursor.x} x2={cursor.x} y1={cursor.top} y2={cursor.bottom} />
            {markers.map((point) => <Dot key={point.key} point={point} />)}
          </g>
        ) : null}
        <rect className={styles.chartHit} x={padding.left} y={padding.top} width={Math.max(10, width - padding.left - padding.right)} height={Math.max(10, height - padding.top - padding.bottom)} />
      </svg>
    </div>
  )
}

// BoardUI chart-card.tsx's rounded swatch legend, retaining a dashed line
// for a previous period so comparison remains distinguishable without color.
export function BoardChartLegend({ items }) {
  return (
    <div className={styles.legend} aria-label="Legend">
      {items.map((item) => (
        <span key={item.key ?? item.label} className={styles.legendItem} data-hoverable={Boolean(item.onEnter) || undefined} {...stateAttributes(item.state)} onMouseEnter={item.onEnter} onMouseLeave={item.onLeave}>
          <span className={styles.legendMark} data-series={item.series} aria-hidden="true" />
          {item.label}
        </span>
      ))}
    </div>
  )
}

// The host owns tooltip lifecycle, formatting, portal and viewport position.
// Shared swatches follow the chart legend; comparison rows keep their pairing.
export function BoardChartTooltip({ tooltipRef, title, rows, position }) {
  return (
    <div ref={tooltipRef} className={styles.tooltip} role="status" style={position ? { top: `${position.top}px`, left: `${position.left}px` } : { top: 0, left: 0, opacity: 0 }}>
      <span className={styles.tooltipTitle}>{title}</span>
      {rows.map((row) => (
        <span key={row.key} className={styles.tooltipRow} data-active={row.active || undefined} data-dashed={row.dashed || undefined} data-paired={row.paired || undefined}>
          <span className={styles.tooltipMark} data-series={row.tone} data-dashed={row.dashed || undefined} aria-hidden="true" />
          <span className={styles.tooltipLabel}>{row.label}</span>
          <strong>{row.value}</strong>
        </span>
      ))}
    </div>
  )
}
